import type { DeliveryGroupDraft, DeliveryGroupStopDraft, LegacyStopDraft } from './deliveryGroupTypes';
import {
  type LoadingUnloadingAllocationLink,
  isLoadingPositionLinkedByAllocations
} from './loadingUnloadingLinkHelpers';

export type RouteStopSaveRow = {
  id?: number | null;
  type: 'loading' | 'unloading';
  position: number;
  pointType?: string | null;
  pointId?: number | null;
  address?: string | null;
  city?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  plannedAt?: string | null;
  actualAt?: string | null;
  notes?: string | null;
  status?: string | null;
  parentLoadingPosition?: number | null;
  parentLoadingStopId?: number | null;
  _stopUid?: string;
  _groupUid?: string;
};

export type RouteStopSaveValidationIssue = {
  code:
    | 'DUPLICATE_POSITION'
    | 'MISSING_POSITION'
    | 'ORPHAN_UNLOADING'
    | 'UNLINKED_LOADING'
    | 'DUPLICATE_STOP_UID';
  message: string;
};

export type MainStopFormFields = {
  id?: number | null;
  /**
   * Execution uid for the main stop row when the primary group has no draft for it.
   * Must be unique per route: execution_stop_uid is globally unique.
   */
  uid?: string;
  address?: string | null;
  city?: string | null;
  contactPerson?: string | null;
  phone?: string | null;
  plannedAt?: string | null;
  actualAt?: string | null;
  pointType?: string | null;
  pointId?: number | null;
  status?: string | null;
};

function stopToSaveRow(
  stop: DeliveryGroupStopDraft,
  groupUid: string,
  type: 'loading' | 'unloading'
): RouteStopSaveRow & { _parentLoadingStopUid?: string } {
  return {
    id: stop.id ?? undefined,
    type,
    position: -1,
    pointType: stop.pointType ?? null,
    pointId: stop.pointId ?? null,
    address: stop.address ?? null,
    city: stop.city ?? null,
    contactPerson: stop.contactPerson ?? null,
    phone: stop.phone ?? null,
    plannedAt: stop.plannedAt ?? null,
    actualAt: stop.actualAt ?? null,
    notes: stop.notes ?? null,
    status: stop.status ?? null,
    parentLoadingPosition: null,
    parentLoadingStopId: stop.parentLoadingStopId ?? null,
    _stopUid: stop.uid,
    _groupUid: groupUid
  };
}

/** Additional stops only — default primary load/unload stay in form fields (position 0 / N+1). */
export function flattenAdditionalStopsFromGroups(groups: DeliveryGroupDraft[]): LegacyStopDraft[] {
  const legacy: LegacyStopDraft[] = [];
  const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);

  for (const group of sorted) {
    if (group.isDefault) {
      for (const l of group.loadingStops.slice(1)) {
        legacy.push({
          uid: l.uid,
          id: l.id ?? null,
          type: 'loading',
          position: -1,
          pointType: l.pointType ?? null,
          pointId: l.pointId ?? null,
          parentLoadingPosition: null,
          groupId: l.groupId ?? group.groupId ?? null,
          groupUid: group.uid,
          address: l.address ?? null,
          city: l.city ?? null,
          contactPerson: l.contactPerson ?? null,
          phone: l.phone ?? null,
          plannedAt: l.plannedAt ?? null,
          actualAt: l.actualAt ?? null,
          notes: l.notes ?? null,
          status: l.status ?? null
        });
      }
      for (const u of group.unloadingStops.slice(1)) {
        legacy.push({
          uid: u.uid,
          id: u.id ?? null,
          type: 'unloading',
          position: -1,
          pointType: u.pointType ?? null,
          pointId: u.pointId ?? null,
          parentLoadingPosition: 0,
          parentLoadingStopId: u.parentLoadingStopId ?? null,
          groupId: u.groupId ?? group.groupId ?? null,
          groupUid: group.uid,
          address: u.address ?? null,
          city: u.city ?? null,
          contactPerson: u.contactPerson ?? null,
          phone: u.phone ?? null,
          plannedAt: u.plannedAt ?? null,
          actualAt: u.actualAt ?? null,
          notes: u.notes ?? null,
          status: u.status ?? null
        });
      }
      continue;
    }

    for (const l of group.loadingStops) {
      legacy.push({
        uid: l.uid,
        id: l.id ?? null,
        type: 'loading',
        position: -1,
        pointType: l.pointType ?? null,
        pointId: l.pointId ?? null,
        parentLoadingPosition: null,
        groupId: l.groupId ?? group.groupId ?? null,
        groupUid: group.uid,
        address: l.address ?? null,
        city: l.city ?? null,
        contactPerson: l.contactPerson ?? null,
        phone: l.phone ?? null,
        plannedAt: l.plannedAt ?? null,
        actualAt: l.actualAt ?? null,
        notes: l.notes ?? null,
        status: l.status ?? null
      });
    }
    for (const u of group.unloadingStops) {
      legacy.push({
        uid: u.uid,
        id: u.id ?? null,
        type: 'unloading',
        position: -1,
        pointType: u.pointType ?? null,
        pointId: u.pointId ?? null,
        parentLoadingPosition: null,
        parentLoadingStopId: u.parentLoadingStopId ?? null,
        groupId: u.groupId ?? group.groupId ?? null,
        groupUid: group.uid,
        address: u.address ?? null,
        city: u.city ?? null,
        contactPerson: u.contactPerson ?? null,
        phone: u.phone ?? null,
        plannedAt: u.plannedAt ?? null,
        actualAt: u.actualAt ?? null,
        notes: u.notes ?? null,
        status: u.status ?? null
      });
    }
  }

  return legacy;
}

export function flattenDeliveryGroupsToLegacyStops(groups: DeliveryGroupDraft[]): LegacyStopDraft[] {
  const additional = flattenAdditionalStopsFromGroups(groups);
  return additional.map((s, idx) => ({ ...s, position: idx + 1 }));
}

export function buildRouteStopsSavePayload(params: {
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
}): RouteStopSaveRow[] {
  const sorted = [...params.groups].sort((a, b) => a.sortOrder - b.sortOrder);
  const primary = sorted.find((g) => g.isDefault) ?? sorted[0];
  const primaryLoading = primary?.loadingStops[0];
  const primaryUnloading = primary?.unloadingStops[0];
  const mainLoadingUid = primaryLoading?.uid ?? params.mainLoading.uid ?? 'main-loading-0';

  const rows: RouteStopSaveRow[] = [];

  rows.push({
    id: primaryLoading?.id ?? params.mainLoading.id ?? undefined,
    type: 'loading',
    position: 0,
    address: params.mainLoading.address ?? primaryLoading?.address ?? null,
    city: params.mainLoading.city ?? primaryLoading?.city ?? null,
    contactPerson: params.mainLoading.contactPerson ?? primaryLoading?.contactPerson ?? null,
    phone: params.mainLoading.phone ?? primaryLoading?.phone ?? null,
    plannedAt: params.mainLoading.plannedAt ?? primaryLoading?.plannedAt ?? null,
    actualAt: params.mainLoading.actualAt ?? primaryLoading?.actualAt ?? null,
    pointType: params.mainLoading.pointType ?? primaryLoading?.pointType ?? null,
    pointId: params.mainLoading.pointId ?? primaryLoading?.pointId ?? null,
    status: params.mainLoading.status ?? primaryLoading?.status ?? null,
    parentLoadingPosition: null,
    _stopUid: mainLoadingUid,
    _groupUid: primary?.uid
  });

  const additionalLoadings: RouteStopSaveRow[] = [];
  const additionalUnloadings: Array<RouteStopSaveRow & { _parentLoadingStopUid?: string }> = [];

  if (primary) {
    for (const l of primary.loadingStops.slice(1)) {
      additionalLoadings.push(stopToSaveRow(l, primary.uid, 'loading'));
    }
    for (const u of primary.unloadingStops.slice(1)) {
      const row = stopToSaveRow(u, primary.uid, 'unloading');
      row._parentLoadingStopUid = mainLoadingUid;
      additionalUnloadings.push(row);
    }
  }

  for (const group of sorted) {
    if (group.isDefault) continue;
    const groupLoadingUid = group.loadingStops[0]?.uid;
    for (const l of group.loadingStops) {
      additionalLoadings.push(stopToSaveRow(l, group.uid, 'loading'));
    }
    for (const u of group.unloadingStops) {
      const row = stopToSaveRow(u, group.uid, 'unloading');
      if (groupLoadingUid) row._parentLoadingStopUid = groupLoadingUid;
      additionalUnloadings.push(row);
    }
  }

  let nextPos = 1;
  const loadingPosByUid = new Map<string, number>();
  loadingPosByUid.set(mainLoadingUid, 0);

  for (const row of additionalLoadings) {
    row.position = nextPos++;
    if (row._stopUid) loadingPosByUid.set(row._stopUid, row.position);
  }

  for (const row of additionalUnloadings) {
    row.position = nextPos++;
    const parentUid = row._parentLoadingStopUid;
    row.parentLoadingPosition =
      parentUid && loadingPosByUid.has(parentUid) ? loadingPosByUid.get(parentUid)! : 0;
  }

  rows.push(...additionalLoadings, ...additionalUnloadings);

  rows.push({
    id: primaryUnloading?.id ?? params.mainUnloading.id ?? undefined,
    type: 'unloading',
    position: nextPos,
    address: params.mainUnloading.address ?? primaryUnloading?.address ?? null,
    city: params.mainUnloading.city ?? primaryUnloading?.city ?? null,
    contactPerson: params.mainUnloading.contactPerson ?? primaryUnloading?.contactPerson ?? null,
    phone: params.mainUnloading.phone ?? primaryUnloading?.phone ?? null,
    plannedAt: params.mainUnloading.plannedAt ?? primaryUnloading?.plannedAt ?? null,
    actualAt: params.mainUnloading.actualAt ?? primaryUnloading?.actualAt ?? null,
    pointType: params.mainUnloading.pointType ?? primaryUnloading?.pointType ?? null,
    pointId: params.mainUnloading.pointId ?? primaryUnloading?.pointId ?? null,
    status: params.mainUnloading.status ?? primaryUnloading?.status ?? null,
    parentLoadingPosition: 0,
    _stopUid: primaryUnloading?.uid ?? params.mainUnloading.uid ?? 'main-unloading',
    _groupUid: primary?.uid
  });

  return rows;
}

/** Stop uids that occur more than once (execution_stop_uid must be unique per stop row). */
export function findDuplicateStopUids(uids: ReadonlyArray<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const raw of uids) {
    const uid = String(raw ?? '').trim();
    if (!uid) continue;
    if (seen.has(uid)) duplicates.add(uid);
    seen.add(uid);
  }
  return Array.from(duplicates);
}

export function formatDuplicateStopUidMessage(duplicates: string[]): string {
  return `Обнаружены дублирующиеся идентификаторы точек маршрута (${duplicates.join(', ')}). Маршрут не отправлен — пересоберите точки маршрута.`;
}

export function validateRouteStopsSavePayload(
  stops: RouteStopSaveRow[],
  options?: { allocationLinks?: LoadingUnloadingAllocationLink[] }
): RouteStopSaveValidationIssue[] {
  const issues: RouteStopSaveValidationIssue[] = [];
  const allocationLinks = options?.allocationLinks ?? [];
  const positions = stops.map((s) => s.position);

  if (positions.some((p) => p < 0 || !Number.isFinite(p))) {
    issues.push({ code: 'MISSING_POSITION', message: 'Обнаружены стопы без корректной позиции' });
  }

  if (new Set(positions).size !== positions.length) {
    issues.push({ code: 'DUPLICATE_POSITION', message: 'Обнаружены дублирующиеся позиции стопов' });
  }

  const duplicateUids = findDuplicateStopUids(stops.map((s) => s._stopUid));
  if (duplicateUids.length > 0) {
    issues.push({ code: 'DUPLICATE_STOP_UID', message: formatDuplicateStopUidMessage(duplicateUids) });
  }

  const loadingPositions = new Set(stops.filter((s) => s.type === 'loading').map((s) => s.position));

  for (const stop of stops) {
    if (stop.type !== 'unloading') continue;
    if (!loadingPositions.has(Number(stop.parentLoadingPosition ?? 0))) {
      issues.push({ code: 'ORPHAN_UNLOADING', message: 'Точка выгрузки не привязана к точке погрузки' });
      break;
    }
  }

  for (const loadingPos of Array.from(loadingPositions)) {
    if (loadingPos === 0) continue;
    const linked = stops.some(
      (s) => s.type === 'unloading' && Number(s.parentLoadingPosition ?? 0) === loadingPos
    );
    if (!linked && isLoadingPositionLinkedByAllocations(loadingPos, allocationLinks)) {
      continue;
    }
    if (!linked) {
      issues.push({
        code: 'UNLINKED_LOADING',
        message: `Дополнительная точка погрузки (позиция ${loadingPos}) не имеет связанной выгрузки`
      });
    }
  }

  return issues;
}

export function prepareStopsCompatPayload(stops: RouteStopSaveRow[]): Array<Record<string, unknown>> {
  return stops.map((s) => {
    const row = { ...s } as RouteStopSaveRow & { _parentLoadingStopUid?: string };
    const clientExecutionUid = row._stopUid;
    const parentLoadingStopUid = row._parentLoadingStopUid;
    delete row._stopUid;
    delete row._groupUid;
    delete row._parentLoadingStopUid;
    return {
      ...row,
      ...(clientExecutionUid ? { clientExecutionUid, stopUid: clientExecutionUid } : {}),
      ...(parentLoadingStopUid ? { parentLoadingStopUid } : {})
    };
  });
}

/** @deprecated Use prepareStopsCompatPayload — keeps uids in API payload for backend round-trip. */
export function stripSaveRowDebugFields(stops: RouteStopSaveRow[]): Array<Record<string, unknown>> {
  return prepareStopsCompatPayload(stops);
}

export function buildStopUidToPositionMap(stops: RouteStopSaveRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const s of stops) {
    if (s._stopUid) map.set(s._stopUid, s.position);
  }
  return map;
}

export function logRouteStopsSaveDebug(
  label: string,
  stops: RouteStopSaveRow[],
  groups?: DeliveryGroupDraft[]
): void {
  if (process.env.NODE_ENV === 'production') return;
  console.group(`[RouteStopsSave] ${label}`);
  console.table(
    stops.map((s) => ({
      uid: s._stopUid,
      id: s.id ?? 'new',
      type: s.type,
      position: s.position,
      pointType: s.pointType,
      pointId: s.pointId,
      parentLoadingPosition: s.parentLoadingPosition
    }))
  );
  console.log('positionMapping', Object.fromEntries(buildStopUidToPositionMap(stops)));
  console.log(
    'parentLoadingLinks',
    stops
      .filter((s) => s.type === 'unloading')
      .map((s) => ({ uid: s._stopUid, position: s.position, parent: s.parentLoadingPosition }))
  );
  if (groups) {
    console.log(
      'groupOwnership',
      groups.map((g) => ({
        uid: g.uid,
        applicationId: g.applicationId,
        loading: g.loadingStops.map((x) => x.uid),
        unloading: g.unloadingStops.map((x) => x.uid)
      }))
    );
  }
  console.groupEnd();
}
